import { Box, Card, MessageBlock } from '@deckee/deck-hand';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Redirect, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { captureEvent } from '../../../../services/event-capture';
import {
  getSelectedCategory,
  resetMarketplaceSearch,
  searchMarketListings,
  selectMarketplaceSearchResults,
} from '../../../../services/store/modules/marketplace';
import BasicDrawerLayout from '../../../layouts/BasicDrawerLayout/BasicDrawerLayout';
import IconWithTextLockup from '../../../lockups/IconWithTextLockup/IconWithTextLockup';
import LogoSpinner from '../../../shared/_deprecated/components/LogoSpinner/LogoSpinner';
import CategoryIcon from '../components/CategoryIcon/CategoryIcon';
import RatingAndCount from '../components/RatingAndCount/RatingAndCount';
import VerifiedBadge from '../components/VerifiedBadge/VerifiedBadge';
import useDistanceFormatting from '../hooks/use-distance-formatting';

const StyledImage = styled.div<{ image: string }>`
  background-image: url(${({ image }) => image});
  height: 80px;
  width: 80px;
  background-repeat: no-repeat;
  background-position: center center;
  background-size: cover;
`;

const MarketplaceSearchResultsPage: FC = () => {
  const searchResults = useSelector(selectMarketplaceSearchResults);
  const selectedCategory = useSelector(getSelectedCategory);
  const selectedCategoryName = selectedCategory?.name;

  const [isLoadingData, setIsLoadingData] = useState(true);
  const [error, setError] = useState<string>();

  // The loading flag should be false if there are search results, regardless of whether we are fetching data
  const loading = searchResults.length > 0 ? false : isLoadingData;

  const dispatch = useDispatch();
  const history = useHistory();

  const searchPromise = useRef<ReturnType<typeof searchMarketListings>>();
  const abortSearch = () => {
    if (searchPromise.current) {
      // @ts-expect-error -- legacy code. abort does not exist on type
      searchPromise.current.abort();
    }
  };

  const formatDistance = useDistanceFormatting();

  useEffect(() => {
    abortSearch();

    const fetchData = async () => {
      searchPromise.current = dispatch(searchMarketListings());

      try {
        await searchPromise.current;
      } catch (e: unknown) {
        setError(
          'Something has gone wrong loading our listings. Please try again soon',
        );
      }
      setIsLoadingData(false);
    };

    fetchData();

    return () => {
      abortSearch();
      dispatch(resetMarketplaceSearch());
    };
  }, [dispatch]);

  const handleClickListing = (name: string) => {
    captureEvent({
      type: 'marketplace: select listing',
      payload: {
        name,
      },
    });
  };

  return selectedCategoryName ? (
    <BasicDrawerLayout
      heading={selectedCategoryName}
      onBack={() => history.push('/maps/marketplace/categories')}
      onClose={null}
    >
      <Box p="3">
        {loading && <LogoSpinner />}

        {!loading && searchResults.length === 0 && (
          <MessageBlock type="warning">
            No {selectedCategoryName} nearby
          </MessageBlock>
        )}

        {error && (
          <MessageBlock type="error" mb="3">
            {error}
          </MessageBlock>
        )}

        {searchResults.map((course) => (
          <Link
            key={course.id}
            to={`/maps/marketplace/listing/${course.id}`}
            onClick={() => handleClickListing(course.name)}
          >
            <Card
              display="flex"
              color="black"
              mb="3"
              pr="2"
              justifyContent="space-between"
              alignItems="center"
            >
              <IconWithTextLockup
                image={
                  course.partner && course.coverImage ? (
                    <StyledImage image={course.coverImage} />
                  ) : (
                    <CategoryIcon
                      renderAsThumbnail
                      iconBaseShape={selectedCategory?.icon}
                    />
                  )
                }
                heading={course.name}
                description={
                  <Box
                    mt={1}
                    display="flex"
                    justifyContent="flex-start"
                    alignItems="center"
                  >
                    {course.starRating !== undefined && (
                      <>
                        <RatingAndCount
                          displayValue={course.starRating}
                          total={course.ratingsTotal || undefined}
                        />
                        &nbsp;&bull;&nbsp;
                      </>
                    )}
                    <Box mr={2}>
                      {formatDistance(course.distanceFromRequestLocation)}
                    </Box>
                  </Box>
                }
                breakWordsInHeading="break-word"
              />
              {course.partner && <VerifiedBadge />}
            </Card>
          </Link>
        ))}
      </Box>
    </BasicDrawerLayout>
  ) : (
    <Redirect to="/maps/marketplace/categories" />
  );
};

export default MarketplaceSearchResultsPage;
